import React from 'react';
import { Box, Button, HStack, Menu, Portal, Text } from '@chakra-ui/react';
import { FiDownload, FiMoreVertical } from 'react-icons/fi';
import PdfNavigationControls from '../shared/PdfNavigationControls';
import PdfDocumentRenderer from '../shared/PdfDocumentRenderer';
import ViewerExpandButton from '../shared/ViewerExpandButton.tsx';
import { ViewerCompactViewerProps, groupHoverOpacity, COMPACT_VIEWER_HIGHLIGHT_BG } from '../shared/types';
import { preventScrolling } from '@/utils/preventScrolling.ts';

const DOCUMENT_PREVIEW_ASPECT = 1.414;
const MIN_PREVIEW_ASPECT = 0.6;
const MAX_PREVIEW_ASPECT = 1.6;
const clampPreviewAspect = (aspect: number): number =>
	Math.min(MAX_PREVIEW_ASPECT, Math.max(MIN_PREVIEW_ASPECT, aspect));

/**
 * Compact PDF viewer component for embedding in chat responses
 *
 * Implements Google Drive-style PDF previews with fixed aspect ratio cropping,
 * yellow hover effects, and overlay controls for better presentation in constrained spaces.
 */
const PdfCompactViewer: React.FC<ViewerCompactViewerProps> = ({
	title,
	maxWidth = 300,
	showExternalLink = false,
	numPages,
	pageNumber,
	loading,
	pdfOptions,
	onDocumentLoadSuccess,
	onDocumentLoadError,
	goToPrevPage,
	goToNextPage,
	openInNewTab,
	onExpand,
	disableHoverEffects = false,
	isHighlighted = false,
	showExpandButton = true,
	showPageControls = true,
	showPreviewHeader = true,
	unstyledCompact = false,
	onPreviewAspectChange,
}) => {
	const [documentPreviewAspect, setDocumentPreviewAspect] = React.useState(DOCUMENT_PREVIEW_ASPECT);
	const previewFrameHeight = maxWidth * (unstyledCompact ? documentPreviewAspect : 0.8);

	const handlePageLoadSuccess = (page: unknown) => {
		const pageWithViewport = page as {
			width?: number;
			height?: number;
			originalWidth?: number;
			originalHeight?: number;
			rotate?: number;
			getViewport?: (options: { scale: number; rotation?: number }) => { width: number; height: number };
		};
		const viewport = pageWithViewport.getViewport?.({ scale: 1, rotation: pageWithViewport.rotate });
		const width = viewport?.width ?? pageWithViewport.originalWidth ?? pageWithViewport.width;
		const height = viewport?.height ?? pageWithViewport.originalHeight ?? pageWithViewport.height;
		if (!width || !height) return;
		const nextAspect = clampPreviewAspect(height / width);
		setDocumentPreviewAspect(nextAspect);
		onPreviewAspectChange?.(nextAspect);
	};

	return (
		<Box
			className="pdf-compact-viewer"
			w={`${maxWidth}px`}
			maxW={`${maxWidth}px`}
			h={unstyledCompact ? previewFrameHeight : undefined}
			aspectRatio={unstyledCompact ? `1 / ${documentPreviewAspect}` : undefined}
			bg={unstyledCompact ? 'transparent' : isHighlighted ? COMPACT_VIEWER_HIGHLIGHT_BG : 'surface.subtle'}
			borderRadius={unstyledCompact ? 0 : '2xl'}
			transition="box-shadow 0.15s ease"
			_hover={disableHoverEffects || unstyledCompact ? {} : { boxShadow: 'md' }}
			my={0}
			overflow="hidden"
		>
			{showPreviewHeader && (
				<Box px={3} py={2} onMouseDown={preventScrolling}>
					<HStack justify="space-between" fontSize="sm">
						<Text fontWeight="medium" color="brand.fg" truncate lineClamp={2}>
							{title ?? 'PDF Preview'}
						</Text>
						{showExternalLink && (
							<Menu.Root positioning={{ placement: 'right-start' }}>
								<Menu.Trigger asChild>
									<Button
										p={0}
										m={0}
										_hover={disableHoverEffects ? {} : { bg: 'brand.muted' }}
										alignSelf="start"
										size="xs"
										rounded="full"
										variant="ghost"
										title="More options"
										onMouseDown={(e) => {
											e.preventDefault();
											e.stopPropagation();
										}}
									>
										<FiMoreVertical />
									</Button>
								</Menu.Trigger>
								<Portal>
									<Menu.Positioner>
										<Menu.Content minW="120px" borderRadius="2xl">
											<Menu.Item value="download-file" borderRadius="xl" onClick={openInNewTab}>
												<FiDownload />
												Download
											</Menu.Item>
										</Menu.Content>
									</Menu.Positioner>
								</Portal>
							</Menu.Root>
						)}
					</HStack>
				</Box>
			)}

			{/* 
				PDF Content Container - Core of the cropping architecture
				
				Uses fixed height (maxWidth * 0.8) with overflow:hidden to crop PDF content
				to desired aspect ratio. This approach is necessary because:
				- PDF.js renders at natural size and cannot be directly aspect-ratio constrained
				- AspectRatio component from Chakra doesn't work with PDF.js rendering behavior
				- Manual height calculation provides precise control over visible content area
				
				The 'group' class enables hover effects for child elements (expand button)
			*/}
			<Box
				p={unstyledCompact ? 0 : 2}
				h={previewFrameHeight}
				overflow="hidden"
				onMouseDown={preventScrolling}
				className="group"
				position="relative"
				cursor="pointer"
			>
				{/* Expand Button */}
				{showExpandButton && <ViewerExpandButton onExpand={onExpand} loading={loading} top={3} right={5} />}

				<Box
					onClick={onExpand}
					_hover={{ cursor: 'pointer' }}
					px={unstyledCompact ? 0 : 2}
					h="full"
					display="flex"
					alignItems="center"
					justifyContent="center"
				>
					<PdfDocumentRenderer
						pdfOptions={pdfOptions}
						pageNumber={pageNumber}
						width={unstyledCompact ? maxWidth : maxWidth - 32}
						loading={loading}
						onLoadSuccess={onDocumentLoadSuccess}
						onLoadError={onDocumentLoadError}
						onPageLoadSuccess={handlePageLoadSuccess}
						containerProps={{
							w: 'full',
							h: unstyledCompact ? 'full' : loading ? '200px' : 'auto',
							minH: loading ? 'full' : undefined,
							borderRadius: 'lg',
							overflow: 'hidden',
						}}
						spinnerSize="md"
					/>
				</Box>

				{/* Navigation Controls */}
				{showPageControls && numPages > 1 && !loading && (
					<Box
						bg="surface.subtle"
						px={4}
						w="100%"
						position="absolute"
						bottom={0}
						right={0}
						zIndex={1}
						{...groupHoverOpacity}
					>
						<PdfNavigationControls
							pageNumber={pageNumber}
							numPages={numPages}
							loading={loading}
							onPrevPage={goToPrevPage}
							onNextPage={goToNextPage}
							variant="compact"
						/>
					</Box>
				)}
			</Box>

			{/* Bottom padding strip for single-page documents, kept outside the overflow
			    container so it stays visible. Matches the card surface (no yellow). */}
			{!loading && !unstyledCompact && <Box bg="surface.subtle" p={2} w="100%" {...groupHoverOpacity} h="16px" />}
		</Box>
	);
};

export default PdfCompactViewer;
